import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { AppState } from '@store/app.state';
import { PaymentActions } from '@store/actions/payment.actions';
import {
  selectVerifyStatus,
  selectVerifyError,
} from '@store/selectors/payment.selectors';
import { AuthService } from '@services/auth.service';
import { VerifyStatus } from '@store/reducers/payment.reducer';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment.html',
  styleUrls: ['./payment.css'],
})
export class PaymentPage implements OnInit {
  // Assigned in constructor so constructor-injected services are available.
  verifyStatus$!: Observable<VerifyStatus>;
  verifyError$!:  Observable<string | null>;
  userEmail$!:    Observable<string | null>;

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.verifyStatus$ = this.store.select(selectVerifyStatus);
    this.verifyError$  = this.store.select(selectVerifyError);
    this.userEmail$    = this.auth.state$.pipe(map(s => s.user?.email ?? null));
  }

  ngOnInit(): void {
    // Apply theme so CSS variables are available (same pattern as HomePage)
    const saved = localStorage.getItem('pxlvd-theme') as 'pxlvd' | 'dark' | 'light' | null;
    document.body.setAttribute('data-theme', saved ?? 'pxlvd');

    const isSuccess = this.route.snapshot.queryParamMap.get('success') === 'true';
    if (!isSuccess) {
      // Direct navigation to /payment without ?success=true - send them to pricing
      this.router.navigate(['/home', 'pricing']);
      return;
    }

    // Wait for AuthService to finish resolving the session before dispatching.
    // Without this, this.auth.user is null at effect-run time even for logged-in
    // users, because getSession() is async and hasn't settled yet.
    this.auth.state$.pipe(
      filter(s => s.initialized),
      take(1),
    ).subscribe(() => {
      this.store.dispatch(PaymentActions.verifyPayment());
    });
  }

  goToStudio(): void {
    this.router.navigate(['/']);
  }

  goToPricing(): void {
    this.router.navigate(['/home', 'pricing']);
  }
}
